import React, { useEffect, useState } from 'react';

interface Rect { top: number; left: number; width: number; height: number }

const PADDING = 6;

function sameRect(a: Rect | null, b: Rect | null) {
  if (!a || !b) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function visibleRect(target: string): Rect | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const topEl = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
  const visible = r.width > 0 && topEl !== null && (el === topEl || el.contains(topEl));
  return visible ? { top: r.top, left: r.left, width: r.width, height: r.height } : null;
}

export default function Spotlight({ targets }: { targets: string[] }) {
  const [rect, setRect] = useState<Rect | null>(null);
  const key = targets.join(',');

  useEffect(() => {
    const list = key ? key.split(',') : [];
    if (list.length === 0) { setRect(null); return; }

    list.map(t => document.querySelector(`[data-tour="${t}"]`)).find(Boolean)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const update = () => {
      const next = list.map(visibleRect).find(Boolean) ?? null;
      setRect(prev => (sameRect(prev, next) ? prev : next));
    };

    update();
    const interval = window.setInterval(update, 200);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [key]);

  if (!rect) return null;

  return (
    <div
      aria-hidden
      className="fixed z-40 pointer-events-none rounded-xl tour-ring transition-all duration-300 ease-out"
      style={{
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }}
    >
      <span className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-amber-500 border-2 border-white" />
      </span>
    </div>
  );
}
